import { Link } from 'react-router-dom';
import { DRINK_PROFILE_LABELS, type BottleDTO, type DrinkProfile } from '@adega/shared';
import { BottleCard } from '../components/BottleCard.js';
import { EmptyState, ErrorBox, Loading } from '../components/Feedback.js';
import { PageHeader } from '../components/PageHeader.js';
import { useCellars, useWaiting } from '../api/queries.js';

/**
 * "Esperando vaga": as garrafas que estão em casa e ainda não têm lugar.
 *
 * Existe porque com as adegas cheias a vaga vai abrir onde a pessoa beber, e não
 * onde ela adivinhou no cadastro. Antes, garrafa sem posição só aparecia dentro
 * de uma adega — e garrafa sem adega nenhuma não aparecia em lugar algum.
 *
 * A ordem vem do backend (`domain/fila.ts`). Aqui só se explica o porquê de cada
 * grupo: uma lista ordenada que não diz o motivo vira adivinhação.
 */

type Grupo = DrinkProfile | 'sem_perfil';

const TITULOS: Record<Grupo, string> = {
  guarda_longa: 'Guarda longa',
  guarda_curta: 'Guarda curta',
  beber_jovem: 'Para beber jovem',
  sem_perfil: 'Sem análise',
};

/*
 * Os motivos falam de "esperar fora da adega", e isso só faz sentido para quem
 * TEM adega: ali a garrafa realmente espera uma vaga. Sem móvel nenhum, a
 * garrafa não espera nada — ela está guardada, e o que muda entre os grupos é
 * quanto cada vinho sofre com isso.
 *
 * Mesmo fato físico (vinho fora de temperatura controlada), duas leituras
 * conforme a pessoa tenha ou não para onde mover.
 */
const MOTIVOS: Record<Grupo, string> = {
  guarda_longa: 'São as que mais perdem esperando fora da adega. Entram primeiro.',
  guarda_curta: 'Ganham alguns anos guardadas, então vale acomodá-las depois das de guarda longa.',
  beber_jovem: 'Vão ser abertas logo: esperar fora da adega custa pouco a elas.',
  sem_perfil:
    'Sem perfil analisado não dá para saber quanto elas perdem esperando. Analise com a IA para entrarem na ordem certa.',
};

const MOTIVOS_SEM_ADEGA: Record<Grupo, string> = {
  guarda_longa: 'São as que mais sofrem guardadas sem controle de temperatura. Abra ou acomode antes.',
  guarda_curta: 'Ganham alguns anos guardadas, mas perdem menos que as de guarda longa.',
  beber_jovem: 'Vão ser abertas logo: o lugar onde estão quase não pesa nelas.',
  sem_perfil:
    'Sem perfil analisado não dá para saber quanto elas perdem. Analise com a IA para entrarem na ordem certa.',
};

const ORDEM_DOS_GRUPOS: Grupo[] = ['guarda_longa', 'guarda_curta', 'beber_jovem', 'sem_perfil'];

function ondeEsta(bottle: BottleDTO, semAdegas: boolean): string {
  if (bottle.cellarName) return `Na ${bottle.cellarName}, sem posição`;
  // "Fora das adegas" pressupõe adegas das quais estar fora.
  return semAdegas ? 'Guardada por aí' : 'Fora das adegas';
}

export function EsperandoVaga() {
  const { data: fila, isLoading, error } = useWaiting();
  const { data: adegas } = useCellars();
  const semAdegas = (adegas?.length ?? 0) === 0;

  if (isLoading) return <Loading texto="Montando a fila…" />;
  // Mesma regra do resto do app: erro só toma a tela quando não há fila para
  // mostrar; com a fila em mãos, quem avisa da queda é a faixa de conexão.
  if (error && !fila) return <ErrorBox erro={error} />;

  if (!fila || fila.length === 0) {
    return (
      <div>
        <PageHeader titulo="Esperando vaga" voltar="/" />
        <EmptyState
          titulo="Ninguém na fila"
          descricao="Toda garrafa cadastrada tem lugar definido. Quando comprar uma sem ter onde guardar, cadastre em “Sem adega” e ela aparece aqui."
          acao={
            <Link to="/adicionar" className="btn-primary">
              Adicionar garrafa
            </Link>
          }
        />
      </div>
    );
  }

  const grupos = ORDEM_DOS_GRUPOS.map((grupo) => ({
    grupo,
    garrafas: fila.filter((bottle) => (bottle.drinkProfile ?? 'sem_perfil') === grupo),
  })).filter((item) => item.garrafas.length > 0);

  return (
    <div className="space-y-4">
      <PageHeader
        /* Sem adega cadastrada, estas garrafas não esperam vaga nenhuma: são
           as garrafas da pessoa, guardadas num armário ou numa caixa. Falar em
           fila ali inventaria uma espera que ela nunca pediu -- e a ordem, que
           continua sendo por quanto cada vinho perde, vira só "o que abrir
           antes", que é útil de qualquer forma. */
        titulo={semAdegas ? 'Suas garrafas' : 'Esperando vaga'}
        subtitulo={
          semAdegas
            ? `${fila.length} ${
                fila.length === 1 ? 'garrafa · guardada' : 'garrafas · guardadas'
              } por aí, sem adega cadastrada`
            : `${fila.length} ${fila.length === 1 ? 'garrafa' : 'garrafas'} · a primeira da fila entra quando abrir lugar`
        }
        voltar="/"
      />

      <p className="text-sm text-adega-muted">
        {semAdegas
          ? 'A ordem é por quanto cada vinho perde sem controle de temperatura — não pela data da compra.'
          : 'A ordem é por quanto cada vinho perde esperando fora da adega — não pela data da compra.'}
      </p>

      {grupos.map(({ grupo, garrafas }) => (
        <section key={grupo} className="space-y-2">
          <div>
            <h2 className="font-semibold">
              {grupo === 'sem_perfil' ? TITULOS.sem_perfil : DRINK_PROFILE_LABELS[grupo]} (
              {garrafas.length})
            </h2>
            <p className="text-xs text-adega-muted">{(semAdegas ? MOTIVOS_SEM_ADEGA : MOTIVOS)[grupo]}</p>
          </div>

          {garrafas.map((bottle) => (
            <BottleCard
              key={bottle.id}
              bottle={bottle}
              extra={<span className="text-xs text-adega-muted">{ondeEsta(bottle, semAdegas)}</span>}
            />
          ))}
        </section>
      ))}
    </div>
  );
}
